import PropTypes from "prop-types";
import HeadMetadata from "app/utils/metas/HeadMetadata";
import {
	getBrandDisplayName,
	getRobotsMetaTag,
	getSeoMetaData,
} from "app/reducers/partnerSelector";
import { useSelector, shallowEqual } from "react-redux";
import { OFFER_PRICE_TYPES } from "app/constants";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { formatAmountForDisplay } from "app/utils/utils";
import appGlobalsContext from "app/AppGlobalsContext";
import { useContext } from "react";
import { getCanonicalUrl, getHomeUrl } from "app/utils/metas/getCanonicalUrl";
import {
	buildBreadcrumbSchema,
	buildGraph,
	buildProductSchema,
	hasReliablePrice,
} from "app/utils/metas/schemaBuilders";
import { renderJsonLd } from "app/utils/metas/JsonLd";

const FicheProduitHeadMetadata = ({ ficheProduit = {}, desktopSlideShowPhotos = [] }) => {
	const {
		robotsMetaTag,
		currencyCode,
		brandDisplayName,
		titleFromSanity,
		descriptionFromSanity,
	} = useSelector(mapStateToProps, shallowEqual);

	let metaDescription = "page.description.product.exclusive";

	const { brand, shop, envVars } = useContext(appGlobalsContext);

	const { pathname, search } = useLocation();

	const intl = useIntl();

	switch (ficheProduit?.fromPriceType?.type) {
		case OFFER_PRICE_TYPES.FROM_PRICE_TYPE_SAVE_UP_TO:
			metaDescription = "page.description.product.saveupto";
			break;
		case OFFER_PRICE_TYPES.FROM_PRICE_TYPE_FROM:
		case OFFER_PRICE_TYPES.FROM_PRICE_TYPE_FROM_WITH_FLIGHT:
			metaDescription = "page.description.product.from";
			break;
		case OFFER_PRICE_TYPES.FROM_PRICE_TYPE_EXCLUSIVE:
			metaDescription = "page.description.product.exclusive";
			break;
		default:
			metaDescription = "page.description.product.catalog";
			break;
	}

	const productName = ficheProduit?.category
		? `${ficheProduit?.name} ${ficheProduit.category}*`
		: ficheProduit?.name;

	const ogImageAlt = `${productName} | ${brandDisplayName}`;

	const amount = intl.formatNumber(ficheProduit?.fromPriceType?.value, {
		style: "currency",
		currency: currencyCode,
		currencyDisplay: "symbol",
		useGrouping: true,
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});

	const pricingValue = formatAmountForDisplay(amount, currencyCode, shop);

	const title = titleFromSanity || "page.title.product";
	const description = descriptionFromSanity || metaDescription;

	// Preview pages must not expose indexable schema (they are noindex).
	const isPreview = search.includes("preview=");

	const canonicalUrl = getCanonicalUrl({
		brand,
		shop,
		baseUrl: envVars.BASE_URL,
		proxyBasenamePrefix: envVars.PROXY_BASENAME_PREFIX,
		pathname,
	});

	const homeUrl = getHomeUrl({
		brand,
		shop,
		baseUrl: envVars.BASE_URL,
		proxyBasenamePrefix: envVars.PROXY_BASENAME_PREFIX,
	});

	const reliablePrice = hasReliablePrice(ficheProduit?.fromPriceType);

	const structuredData = isPreview
		? null
		: buildGraph(
				buildBreadcrumbSchema({
					items: [
						{ name: intl.formatMessage({ id: "faq.breadcrumb.home" }), url: homeUrl },
						{ name: productName, url: canonicalUrl },
					],
				}),
				buildProductSchema({
					name: productName,
					url: canonicalUrl,
					sku: ficheProduit?.id,
					image: desktopSlideShowPhotos?.[0]?.url,
					price: reliablePrice ? ficheProduit?.fromPriceType?.value : undefined,
					priceCurrency: reliablePrice ? currencyCode : undefined,
				})
			);

	return (
		<HeadMetadata
			description={description}
			descriptionAttributes={{
				pricingValue,
				country: ficheProduit?.country,
			}}
			title={title}
			titleAttributes={{ name: ficheProduit.name, category: ficheProduit.category }}
			robotsMetaTag={robotsMetaTag}
			imageUrl={
				desktopSlideShowPhotos && desktopSlideShowPhotos[0]
					? desktopSlideShowPhotos[0].url
					: ""
			}
		>
			<meta property="og:image:alt" content={ogImageAlt} />
			{renderJsonLd(structuredData)}
		</HeadMetadata>
	);
};

FicheProduitHeadMetadata.propTypes = {
	ficheProduit: PropTypes.object,
	currencyCode: PropTypes.string,
	brandDisplayName: PropTypes.string,
	desktopSlideShowPhotos: PropTypes.array,
	robotsMetaTag: PropTypes.array,
	titleFromSanity: PropTypes.string,
	descriptionFromSanity: PropTypes.string,
};

const mapStateToProps = state => {
	const robotsMetaTag = getRobotsMetaTag(state);
	return {
		robotsMetaTag: robotsMetaTag?.product,
		brandDisplayName: getBrandDisplayName(state),
		currencyCode: state?.currency,
		titleFromSanity: getSeoMetaData("productPagePP.metaTitle")(state),
		descriptionFromSanity: getSeoMetaData("productPagePP.metaDescription")(state),
	};
};

export default FicheProduitHeadMetadata;
